import { FC, JSX, useContext, useMemo } from 'react';
import { LngLat, Popup } from "react-map-gl/mapbox";
import { MarketExpansionMapDataContext } from '@app/overviews/market-expansion-dashboard/contexts/MarketExpansionMapDataContext';
import { ContractEntityColorTheme } from '@app/overviews/market-expansion-dashboard/contexts/ContractEntityColorThemeContext';
import { ProviderLandscapeMapDataContext } from '@app/overviews/market-expansion-dashboard/contexts/ProviderLandscapeMapDataContext';
import { createTooltipHeightObserver } from '@app/overviews/market-expansion-dashboard/shared/marketExpansionMapHelper';
import { EllipsisTooltip } from '@app/shared/EllipsisTooltip';
import { QesIcon } from '@app/shared/icons/QESIcon';
import { QesIcons } from '@app/shared/icons/qesIcons';

const ProviderLandscapeCountySummary: FC<{ isCountySelectionPopup: boolean }> = ({ isCountySelectionPopup }): JSX.Element => {
  const mapDataContext = useContext(MarketExpansionMapDataContext);
  const providerLandscapeMapDataContext = useContext(ProviderLandscapeMapDataContext);

  const { getColor } = ContractEntityColorTheme.useMapColorTheme();

  const countyInfo = isCountySelectionPopup ?
    providerLandscapeMapDataContext.countySummaryData?.find(x => x.countySSA == mapDataContext.selectedCountySSA) :
    providerLandscapeMapDataContext.countySummaryData?.find(x => x.countySSA == mapDataContext.hoveredCountySSA);

  const showFullCountyInfo = !mapDataContext.selectedCountySSA || isCountySelectionPopup;

  const selectedCountyCenter = useMemo<LngLat | null | undefined>(() => {
    if (!mapDataContext.selectedCountySSA) {
      return null;
    }
    return mapDataContext.getBoundingBoxFeature(
      providerLandscapeMapDataContext.providerLandscapeShadedCountyData?.features.find(
        x => x.properties.countySSA == mapDataContext.selectedCountySSA
      ))?.getCenter();
  }, [mapDataContext.selectedCountySSA]);

  const coordinates = isCountySelectionPopup ? selectedCountyCenter : mapDataContext.mouseCoordinates;

  const handlePopupClose = () => {
    mapDataContext.setSelectedCountySSA(null);
  };

  const { setSelectedTooltipHeight } = mapDataContext;

  const registerTooltipContent = useMemo(
    () => createTooltipHeightObserver(setSelectedTooltipHeight),
    [setSelectedTooltipHeight]
  );

  const sortedContractEntities = countyInfo?.contractEntities ?
    countyInfo.contractEntities
      .slice()
      .sort((a, b) => b.caseCount - a.caseCount)
      .slice(0, 5)
    : [];

  const getContractEntityColorIcon = (groupingId: number) => (
    <QesIcon
      className="me-2 align-text-top"
      iconName={QesIcons.Circle}
      isDecorative
      pathStyle={{
        fill: getColor(providerLandscapeMapDataContext.getContractEntityColorSlotKey(groupingId)),
        strokeWidth: 0,
      }}
      height="14"
    />
  );

  const showHoverPopup = !isCountySelectionPopup && !!countyInfo && countyInfo.countySSA !== mapDataContext.selectedCountySSA;
  const showSelectionPopup = (isCountySelectionPopup && !!mapDataContext.selectedCountySSA);

  if (!showFullCountyInfo) {
    return (
      <>
        {(showHoverPopup || showSelectionPopup) && coordinates &&
          <Popup
            closeButton={isCountySelectionPopup}
            onClose={handlePopupClose}
            closeOnClick={false}
            latitude={coordinates.lat}
            longitude={coordinates.lng}
            offset={[0, 0]}
          >
            <div className="county-name-popup" data-testid="provider-landscape-map__county-name-popup">
              <strong className="county-name">{countyInfo?.countyName}</strong>
            </div>
          </Popup>
        }
      </>
    );
  }

  return (
    <>
      {(showHoverPopup || showSelectionPopup) && coordinates && (
        <Popup
          anchor={isCountySelectionPopup ? "bottom" : undefined}
          closeButton={isCountySelectionPopup}
          onClose={handlePopupClose}
          closeOnClick={false}
          latitude={coordinates.lat}
          longitude={coordinates.lng}
          offset={[0, 0]}
          className="county-summary-popup provider-landscape-popup"
        >
          <div data-testid="provider-landscape-map__county-summary-popup" ref={isCountySelectionPopup ? registerTooltipContent : undefined}>
            <div className="county-summary-popup__title">
              <strong className="county-name">{countyInfo?.countyName} County</strong>
              <span className="total-enrollment-count">Cases: {countyInfo?.totalCaseCount ? countyInfo.totalCaseCount.toLocaleString() : "---"}</span>
            </div>
            {!!countyInfo && countyInfo.totalCaseCount > 0 && (
              <table className="county-summary-popup__table">
                <thead>
                  <tr>
                    <th className="contract-entity-title column-title">Contract Entity</th>
                    <th className="number-cases-title column-title"># of Cases</th>
                    <th className="percent-cases-title column-title">% of Cases</th>
                  </tr>
                  <tr>
                    <td colSpan={3}>
                      <div className="county-summary-popup__separator" />
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {sortedContractEntities.map((contractEntity) => {
                    return (
                      <tr key={contractEntity.contractEntityName}>
                        <td className="contract-entity-data">
                          {getContractEntityColorIcon(contractEntity.groupingId)}
                          <span className="contract-entity-name">
                            <EllipsisTooltip text={contractEntity.contractEntityName} width="100%" position="top" />
                          </span>
                        </td>
                        <td className="number-cases-data">{contractEntity.caseCount.toLocaleString()}</td>
                        <td className="percent-cases-data">
                          {`${Math.round((contractEntity.caseCount / countyInfo.totalCaseCount) * 100)}%`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </Popup>
      )}
    </>
  );
};

export default ProviderLandscapeCountySummary;